import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import {
    EntrepreneurIncome,
    EntrepreneurIncomeForm,
    IncomeModel,
    IncomeType,
    IncomeTypeForm,
    MonthlyIncome,
    MonthlyIncomeForm
} from './income.model';

/**
 * @ngdoc service
 * @name ApIncomeService
 * @description
 * The ApIncomeService can be used to construct form controls to be used with {@link ApIncomeComponent}
 */
@Injectable()
export class ApIncomeService {
    private readonly monthlyValue: number = 12;
    private readonly fourWeeklyValue: number = 13;
    private readonly yearlyVacationValue: number = 0.08;
    private readonly onlyTwoFinacialYearsPenalty: number = 0.9;
    private readonly onlyOneFinacialYearPenalty: number = 0.75;

    public createFormGroup(income: IncomeModel = {} as IncomeModel): FormGroup {
        return new FormGroup({
            yearlyIncome: new FormControl(income.yearlyIncome, {
                validators: [Validators.required, Validators.max(500000)]
            })
        });
    }
    public createIncomeTypeForm(incomeType: IncomeType = 'employment') {
        return new FormGroup<IncomeTypeForm>({
            type: new FormControl(incomeType)
        });
    }
    public createMonthlyIncomeForm(monthlyIncome: MonthlyIncome = {} as MonthlyIncome): FormGroup<MonthlyIncomeForm> {
        return new FormGroup({
            monthlyIncome: new FormControl(monthlyIncome.monthlyIncome, {
                validators: [Validators.required]
            }),
            hasVacationPayment: new FormControl(monthlyIncome.hasVacationPayment),
            hasThirteenthMonthPayment: new FormControl(monthlyIncome.hasThirteenthMonthPayment),
            hasBonusPayment: new FormControl(monthlyIncome.hasBonusPayment),
            bonusPayment: new FormControl(monthlyIncome.bonusPayment),
            frequency: new FormControl(monthlyIncome.frequency || 'monthly')
        });
    }

    public createEntrepreneurIncomeForm(
        entrepreneurIncome: EntrepreneurIncome = {} as EntrepreneurIncome
    ): FormGroup<EntrepreneurIncomeForm> {
        return new FormGroup({
            nrOfFinancialYears: new FormControl(entrepreneurIncome.nrOfFinancialYears || 3),
            taxableProfitLastYear: new FormControl(entrepreneurIncome.taxableProfitLastYear),
            taxableProfitLastYearMinusOne: new FormControl(entrepreneurIncome.taxableProfitLastYearMinusOne),
            taxableProfitLastYearMinusTwo: new FormControl(entrepreneurIncome.taxableProfitLastYearMinusTwo)
        });
    }

    public calculateAnnualIncome(monthlyIncome: MonthlyIncome, numberOfDecimals: number): string {
        const monthlyIncomeMultiplier = monthlyIncome.frequency === 'fourWeekly' ? this.fourWeeklyValue : this.monthlyValue;

        let yearlyIncome = Number(monthlyIncome.monthlyIncome) * monthlyIncomeMultiplier;

        let vacationBonus = 0;
        if (monthlyIncome.hasVacationPayment) {
            vacationBonus = yearlyIncome * this.yearlyVacationValue;
        }

        let thirteenthMonthBonus = 0;
        if (monthlyIncome.hasThirteenthMonthPayment) {
            thirteenthMonthBonus = yearlyIncome / this.monthlyValue;
        }

        yearlyIncome += vacationBonus;
        yearlyIncome += thirteenthMonthBonus;

        if (monthlyIncome.hasBonusPayment) {
            yearlyIncome += Number(monthlyIncome.bonusPayment);
        }
        return isNaN(yearlyIncome) ? undefined : `${yearlyIncome.toFixed(numberOfDecimals)}`;
    }

    public calculateAnnualIncomeEntrepreneur(entrepreneurIncome: EntrepreneurIncome, numberOfDecimals: number): string {
        if (Number(entrepreneurIncome.nrOfFinancialYears) === 3) {
            const lastYearMinusTwo = Number(entrepreneurIncome.taxableProfitLastYearMinusTwo);
            const lastYearMinusOne = Number(entrepreneurIncome.taxableProfitLastYearMinusOne);
            const lastYear = Number(entrepreneurIncome.taxableProfitLastYear);
            const averageOverYears = (lastYearMinusTwo + lastYearMinusOne + lastYear) / entrepreneurIncome.nrOfFinancialYears;

            return Math.min(lastYear, averageOverYears).toFixed(numberOfDecimals);
        }
        if (Number(entrepreneurIncome.nrOfFinancialYears) === 2) {
            const lastYearMinusOne = Number(entrepreneurIncome.taxableProfitLastYearMinusOne);
            const lastYear = Number(entrepreneurIncome.taxableProfitLastYear);
            const averageOverYears = (lastYearMinusOne + lastYear) / entrepreneurIncome.nrOfFinancialYears;
            const averageWithPenalty = averageOverYears * this.onlyTwoFinacialYearsPenalty;

            return Math.min(lastYear, averageWithPenalty).toFixed(numberOfDecimals);
        }
        if (Number(entrepreneurIncome.nrOfFinancialYears) === 1) {
            const lastYear = Number(entrepreneurIncome.taxableProfitLastYear);
            const lastYearWithPenalty = lastYear * this.onlyOneFinacialYearPenalty;

            return Math.min(lastYear, lastYearWithPenalty).toFixed(numberOfDecimals);
        }
        return;
    }
}
